import {AbstractControl, FormArray, FormControl, FormGroup, Validators} from "@angular/forms";
import {sumOfCompositeFractionsValidator} from "./validators";
import {
  Adhesive,
  CompositeData,
  Finishing,
  FinishingType,
  MasterData, Material,
  PackagingContent,
  PackagingData,
  PackagingDescription,
  PackagingInfoData,
  PackagingItemsRequest,
  PackagingStatus,
  PartData,
  TemplateFilter
} from "./generated/types";
import {Credentials, MaterialAttributes} from "./types";
import {supportedLanguages} from "./helpers";
import {calculateDensity} from "./calculateDensity";

export const toTranslationsForm = <T>(translations: { [language: string]: T }, toForm: (translation: T) => AbstractControl) => {

  const form = new FormGroup({});

  for (let language of supportedLanguages) {
    form.addControl(language, toForm(translations[language]));
  }

  return form;
};

export const toCredentialsForm = (credentials: Credentials) => new FormGroup({
  name: new FormControl(credentials.name, [Validators.required]),
  password: new FormControl(credentials.password, [Validators.required])
});

export const toPackagingDescriptionForm = (description: PackagingDescription, status: PackagingStatus) => {

  const form = new FormGroup({
    projectId: new FormControl(description.projectId, [Validators.required]),
    name: new FormControl(description.name, [Validators.required]),
    product: new FormControl(description.product),
    templateId: new FormControl(description.templateId)
  });

  updatePackagingDescriptionData(form, status)

  return form;
}

export const toCompositeDataForm = (data: CompositeData) => new FormGroup({
  id: new FormControl(data.id),
  material: new FormControl(data.material, [Validators.required]),
  fraction: new FormControl(data.fraction, [Validators.required, Validators.min(0.001), Validators.max(0.999)])
});

export const toCompositeDataFormArray = (data: CompositeData[]) => new FormArray(data.map(toCompositeDataForm), [ sumOfCompositeFractionsValidator]);


function updateConditionalForm<T extends AbstractControl>(condition: boolean, formGroup: FormGroup, options: { name: string; create: () => T; update?: (form: T) => void; }) {
  if (condition) {
    if (formGroup.controls[options.name] == null) {
      formGroup.addControl(options.name, options.create());
    } else {
      if (options.update) {
        options.update(formGroup.controls[options.name] as T);
      }
    }
  } else if (!condition && formGroup.controls[options.name] != null) {
    formGroup.removeControl(options.name);
  }
}

export function updatePartDataForm(formGroup: FormGroup, materials: Record<Material, MaterialAttributes>, data: PartData) {

  const calculatedDensity = calculateDensity(materials, data);

  const lowForm = formGroup.controls.materialDensityLow as FormControl;
  const highForm = formGroup.controls.materialDensityHigh as FormControl;

  if (calculatedDensity == null) {
    lowForm.setValidators([Validators.required]);
    highForm.setValidators([Validators.required]);
  } else {
    lowForm.setValidators([]);
    highForm.setValidators([]);
  }

  lowForm.updateValueAndValidity({ emitEvent: false });
  highForm.updateValueAndValidity({ emitEvent: false });

  return calculatedDensity;
}


export function updateFinishingForm(formGroup: FormGroup, finishing: Finishing, isInitial = false) {

  const show = finishing.type !== FinishingType.None;
  const showThickness = finishing.type == FinishingType.Lacquering;

  updateConditionalForm(show, formGroup, {
    name: 'surface',
    create() {
      return new FormControl(isInitial ? finishing.surface : 1, Validators.compose([Validators.required, Validators.min(0), Validators.max(1)]));
    }
  });

  updateConditionalForm(showThickness, formGroup, {
    name: 'thickness',
    create() {
      return new FormControl(isInitial ? finishing.thickness : 5, Validators.compose([Validators.required, Validators.min(0), Validators.max(30)]));
    }
  });

  updateConditionalForm(show, formGroup, {
    name: 'containsMetal',
    create() {
      return new FormControl(isInitial ? finishing.containsMetal : false);
    }
  });

  updateConditionalForm(show, formGroup, {
    name: 'isTransparent',
    create() {
      return new FormControl(isInitial ? finishing.isTransparent : false);
    }
  });
}

export const toFinishingForm = (finishing: Finishing) => {

  const formGroup = new FormGroup({
    type: new FormControl(finishing.type)
  });

  updateFinishingForm(formGroup, finishing, true);

  return formGroup;
}

export const toAdhesiveForm = (adhesive: Adhesive) => {

  const formGroup = new FormGroup({
    type: new FormControl(adhesive.type),
    fraction: new FormControl(adhesive.fraction, [Validators.max(0.5)]),
    application: new FormControl(adhesive.application)
  });

  return formGroup;
}

export const toPartDataForm = (materials: Record<Material, MaterialAttributes>, data: PartData) => {

  const form = new FormGroup<any>({
    id: new FormControl(data.id),
    name: new FormControl(data.name),
    component: new FormControl(data.component, [Validators.required]),
    materialFamily: new FormControl(data.materialFamily, [Validators.required]),
    material: new FormControl(data.material, [Validators.required]),
    densityModification: new FormControl(data.densityModification),
    materialDensityLow: new FormControl(data.materialDensityLow),
    materialDensityHigh: new FormControl(data.materialDensityHigh),
    color: new FormControl(data.color, [Validators.required]),
    ncBasedPrinting: new FormControl(data.ncBasedPrinting),
    adhesive: toAdhesiveForm(data.adhesive),
    finishing: toFinishingForm(data.finishing),
    shape: new FormControl(data.shape),
    x: new FormControl(data.x, [Validators.required, Validators.min(0.01)]),
    y: new FormControl(data.y, [Validators.required, Validators.min(0.01)]),
    z: new FormControl(data.z, [Validators.required, Validators.min(0.01)]),
    weight: new FormControl(data.weight, [Validators.required, Validators.min(0.01)]),
  });

  updatePartDataForm(form, materials, data);

  if (data.composites) {
    form.addControl('composites', toCompositeDataFormArray(data.composites));
  }

  return form;
}

export function updatePackagingInfoData(form: FormGroup, data: PackagingInfoData) {

  const gtinForm = form.controls.articles as FormControl;
  const referenceNrForm = form.controls.referenceNr as FormControl;

  if (data.status === PackagingStatus.Circulation) {
    gtinForm.setValidators([Validators.required]);
    referenceNrForm.setValidators([Validators.required, Validators.maxLength(20)]);
  } else {
    gtinForm.setValidators([]);
    referenceNrForm.setValidators([Validators.maxLength(20)]);
  }

  gtinForm.updateValueAndValidity({ emitEvent: false });
  referenceNrForm.updateValueAndValidity({ emitEvent: false });
}

export function updatePackagingDescriptionData(form: FormGroup, status: PackagingStatus) {

  const productForm = form.controls.product as FormControl;

  if (status === PackagingStatus.Circulation) {
    productForm.setValidators([Validators.required]);
  } else {
    productForm.setValidators([]);
  }

  productForm.updateValueAndValidity({ emitEvent: false });
}

export function toPackagingContentForm(data: PackagingContent) {
  return new FormGroup({
    type: new FormControl(data.type),
    volume: new FormControl(data.volume, Validators.min(0)),
    mass: new FormControl(data.mass, Validators.min(0)),
    isRadioactive: new FormControl(data.isRadioactive),
    isInfectious: new FormControl(data.isInfectious),
    isOtherHazardous: new FormControl(data.isOtherHazardous),
    isSealant: new FormControl(data.isSealant)
  });
}

export function toPackagingInfoData(data: PackagingInfoData) {
  const form = new FormGroup({
    ownerId: new FormControl(data.ownerId, [Validators.required]),
    status: new FormControl(data.status, [Validators.required]),
    productTypeId: new FormControl(data.productTypeId),
    referenceNr: new FormControl(data.referenceNr),
    content: toPackagingContentForm(data.content),
    customerArticleNr: new FormControl(data.customerArticleNr, Validators.maxLength(12)),
    customerArticleVersion: new FormControl(data.customerArticleVersion, Validators.maxLength(5)),
    assumption: new FormControl(data.assumption),
    remarks: new FormControl(data.remarks),
    observation: new FormControl(data.observation),
    articles: new FormControl(data.articles),
    defaultCalculation: new FormControl(data.defaultCalculation, Validators.required),
    isTemplate: new FormControl(data.isTemplate),
    classifications: new FormControl(data.classifications)
  });

  updatePackagingInfoData(form, data);

  return form;
}

export const toPackagingDataForm = (materials: Record<Material, MaterialAttributes>, data: PackagingData) => {

  const description = toPackagingDescriptionForm(data.description, data.info.status);
  const info = toPackagingInfoData(data.info);

  return new FormGroup({
    description,
    parts: new FormArray(data.parts.map(part => toPartDataForm(materials, part))),
    relations: new FormControl(data.relations),
    image: new FormControl(data.image),
    images: new FormArray(data.images.map(image => new FormControl(image))),
    attachments: new FormArray(data.attachments.map(attachment => new FormControl(attachment))),
    info,
    referencedAsTemplateBy: new FormControl(data.referencedAsTemplateBy),
    proposals: new FormControl(data.proposals || [])
  });

};

export const markAsTouched = (control: AbstractControl) => {
  control.markAsTouched();
  if (control instanceof FormGroup) {
    for (let key in control.controls) { markAsTouched(control.controls[key]); }
  } else if (control instanceof FormArray) {
    control.controls.forEach(markAsTouched);
  }
}


export function toPackagingItemsRequestForm(request: PackagingItemsRequest) {
  return new FormGroup({
    filter: new FormGroup({
      ownerId: new FormControl(request.filter.ownerId),
      text: new FormControl(request.filter.text),
      projectId: new FormControl(request.filter.projectId),
      classifications: new FormControl(request.filter.classifications)
    }),
    sorting: new FormGroup({
      property: new FormControl(request.sorting.property),
      direction: new FormControl(request.sorting.direction)
    }),
    skip: new FormControl(request.skip),
    take: new FormControl(request.take)
  });
}

export function toTemplateFilterForm(filter: TemplateFilter) {
  return new FormGroup({
    search: new FormControl(filter.search),
    classifications: new FormControl(filter.classifications)
  });
}
